/**
 * Vuex store module for the inquiry wizard
 * Manages wizard state, current step, and inquiry type
 */
import Vue from 'vue';
import ActionService from '@/services/ActionService';
import AdministationService from '@/services/AdministationService';
import AuthenticationService from '@/services/AuthenticationService';
import { DRAFT_SCHEMA_VERSION, migrateDraft, fileToBase64, base64ToFile } from './draftSerializer';

const state = {
  currentStep: 0, // 0-5, step 0 is entry, steps 1-5 are wizard steps
  inquiryType: null, // 'complaint', 'general', or 'report'
  selectedCheckId: null, // Store selected check (survey instance ID) from Step 1
  surveyRevisionCreationLimitInDays: null, // Deadline for creating revisions (from client settings or check-specific)
  selectedQuestionIds: [], // Array of SurveyQuestionId selected in Step 3
  wizardData: {}, // Store form data from each step
  questionComplaints: {}, // Step 4: Store complaint data for each question: { [questionId]: { desiredAnswers: [], justification: '', attachments: [] } }
  questionAttachmentMandatory: {}, // Stores IsAttachmentMandatory per questionId (from backend)
  questionIsCommentPossible: {}, // Stores IsCommentPossible per questionId (from backend)
  questionHasChoices: {}, // Stores whether a question has more than one answer option (from backend)
  questionAiAnalysis: {}, // Stores AI analysis result per questionId (persisted across step navigation)
  // Step 5: Confirmation checkboxes
  confirmations: {
    correctness: false,
    privacy: false,
    noChanges: false,
  },
  // Step 5: Submission state
  isSubmitting: false,
  submissionError: null,
  hasUploadFailures: false,
  createdSurveyRevisionId: null,
  createdEntryMappings: [],
  // Draft state
  isSavingDraft: false,
  draftSaveError: null,
};

// Helper function to ensure question complaint exists
const ensureQuestionComplaint = (state, questionId) => {
  if (!state.questionComplaints[questionId]) {
    Vue.set(state.questionComplaints, questionId, {
      desiredAnswers: [],
      justification: '',
      attachments: [],
    });
  }
};

const mutations = {
  SET_STEP(state, step) {
    state.currentStep = step;
  },
  
  SET_INQUIRY_TYPE(state, type) {
    state.inquiryType = type;
  },
  
  SET_SELECTED_CHECK(state, checkId) {
    state.selectedCheckId = checkId;
  },
  
  SET_SURVEY_REVISION_CREATION_LIMIT_IN_DAYS(state, days) {
    state.surveyRevisionCreationLimitInDays = days;
  },
  
  SET_WIZARD_DATA(state, { step, data }) {
    state.wizardData = {
      ...state.wizardData,
      [step]: data,
    };
  },
  
  SET_SELECTED_QUESTION_IDS(state, questionIds) {
    state.selectedQuestionIds = questionIds;
  },
  
  ADD_SELECTED_QUESTION(state, questionId) {
    if (!state.selectedQuestionIds.includes(questionId)) {
      state.selectedQuestionIds.push(questionId);
    }
  },
  
  REMOVE_SELECTED_QUESTION(state, questionId) {
    state.selectedQuestionIds = state.selectedQuestionIds.filter(id => id !== questionId);
  },
  
  SET_QUESTION_DESIRED_ANSWERS(state, { questionId, desiredAnswers }) {
    ensureQuestionComplaint(state, questionId);
    Vue.set(state.questionComplaints[questionId], 'desiredAnswers', desiredAnswers);
  },
  
  SET_QUESTION_JUSTIFICATION(state, { questionId, justification }) {
    ensureQuestionComplaint(state, questionId);
    Vue.set(state.questionComplaints[questionId], 'justification', justification);
  },
  
  ADD_QUESTION_ATTACHMENT(state, { questionId, attachment }) {
    ensureQuestionComplaint(state, questionId);
    const newAttachments = [...state.questionComplaints[questionId].attachments, attachment];
    Vue.set(state.questionComplaints[questionId], 'attachments', newAttachments);
  },
  
  UPDATE_QUESTION_ATTACHMENT(state, { questionId, attachmentIndex, attachment }) {
    if (state.questionComplaints[questionId]?.attachments[attachmentIndex]) {
      const newAttachments = [...state.questionComplaints[questionId].attachments];
      newAttachments[attachmentIndex] = attachment;
      Vue.set(state.questionComplaints[questionId], 'attachments', newAttachments);
    }
  },
  
  REMOVE_QUESTION_ATTACHMENT(state, { questionId, attachmentIndex }) {
    if (state.questionComplaints[questionId]?.attachments) {
      const newAttachments = state.questionComplaints[questionId].attachments.filter((_, index) => index !== attachmentIndex);
      Vue.set(state.questionComplaints[questionId], 'attachments', newAttachments);
    }
  },
  
  RESET_WIZARD(state) {
    state.currentStep = 0;
    state.inquiryType = null;
    state.selectedCheckId = null;
    state.surveyRevisionCreationLimitInDays = null;
    state.selectedQuestionIds = [];
    state.wizardData = {};
    state.questionComplaints = {};
    state.questionAttachmentMandatory = {};
    state.questionIsCommentPossible = {};
    state.questionHasChoices = {};
    state.questionAiAnalysis = {};
    state.confirmations = { correctness: false, privacy: false, noChanges: false };
    state.isSubmitting = false;
    state.submissionError = null;
    state.hasUploadFailures = false;
    state.createdSurveyRevisionId = null;
    state.createdEntryMappings = [];
    state.isSavingDraft = false;
    state.draftSaveError = null;
  },

  SET_CONFIRMATION(state, { key, value }) {
    Vue.set(state.confirmations, key, value);
  },

  SET_IS_SUBMITTING(state, value) {
    state.isSubmitting = value;
  },

  SET_SUBMISSION_ERROR(state, error) {
    state.submissionError = error;
  },

  SET_HAS_UPLOAD_FAILURES(state, value) {
    state.hasUploadFailures = value;
  },

  SET_CREATED_SURVEY_REVISION_ID(state, id) {
    state.createdSurveyRevisionId = id;
  },

  SET_CREATED_ENTRY_MAPPINGS(state, mappings) {
    state.createdEntryMappings = mappings;
  },

  SET_QUESTION_ATTACHMENT_MANDATORY(state, { questionId, isAttachmentMandatory }) {
    Vue.set(state.questionAttachmentMandatory, questionId, isAttachmentMandatory);
  },

  SET_QUESTION_IS_COMMENT_POSSIBLE(state, { questionId, isCommentPossible }) {
    Vue.set(state.questionIsCommentPossible, questionId, isCommentPossible);
  },

  SET_QUESTION_HAS_CHOICES(state, { questionId, hasChoices }) {
    Vue.set(state.questionHasChoices, questionId, hasChoices);
  },

  SET_QUESTION_AI_ANALYSIS(state, { questionId, result }) {
    Vue.set(state.questionAiAnalysis, questionId, result);
  },

  SET_ATTACHMENT_UPLOAD_STATUS(state, { questionId, attachmentIndex, uploadStatus, uploadError }) {
    if (state.questionComplaints[questionId]?.attachments[attachmentIndex]) {
      const attachment = { ...state.questionComplaints[questionId].attachments[attachmentIndex] };
      attachment.uploadStatus = uploadStatus;
      attachment.uploadError = uploadError || null;
      const newAttachments = [...state.questionComplaints[questionId].attachments];
      newAttachments[attachmentIndex] = attachment;
      Vue.set(state.questionComplaints[questionId], 'attachments', newAttachments);
    }
  },

  SET_IS_SAVING_DRAFT(state, value) {
    state.isSavingDraft = value;
  },

  SET_DRAFT_SAVE_ERROR(state, error) {
    state.draftSaveError = error;
  },
};

const actions = {
  initializeWizard({ commit, dispatch }, inquiryType) {
    commit('RESET_WIZARD');
    commit('SET_INQUIRY_TYPE', inquiryType);
    commit('SET_STEP', 0);
    dispatch('loadClientDefaultSurveyRevisionCreationLimitInDays');
  },
  
  navigateToStep({ commit }, step) {
    commit('SET_STEP', step);
  },
  
  nextStep({ state, commit }) {
    if (state.currentStep < 5) {
      commit('SET_STEP', state.currentStep + 1);
    }
  },
  
  previousStep({ state, commit }) {
    if (state.currentStep > 0) {
      commit('SET_STEP', state.currentStep - 1);
    }
  },
  
  saveStepData({ commit }, { step, data }) {
    commit('SET_WIZARD_DATA', { step, data });
  },
  
  selectCheck({ commit }, checkId) {
    commit('SET_SELECTED_CHECK', checkId);
  },
  
  setSurveyRevisionCreationLimitInDays({ commit }, days) {
    commit('SET_SURVEY_REVISION_CREATION_LIMIT_IN_DAYS', days);
  },

  // Loads the client default for the revision creation limit, used when neither the
  // summary nor a selected check has provided a more specific value
  async loadClientDefaultSurveyRevisionCreationLimitInDays({ state, commit }) {
    if (state.surveyRevisionCreationLimitInDays)
      return;

    if (AuthenticationService.isMSMUser || !AuthenticationService.localUserProfile.ClientId)
      return;

    try {
      const response = await AdministationService.getClientSettings(AuthenticationService.localUserProfile.ClientId);
      const days = response.data?.[0]?.SurveyRevisionCreationLimitInDays;
      if (days)
        commit('SET_SURVEY_REVISION_CREATION_LIMIT_IN_DAYS', days);
    } catch (error) {
      console.error('Error loading client settings for revision creation limit:', error);
    }
  },

  setSelectedQuestionIds({ commit }, questionIds) {
    commit('SET_SELECTED_QUESTION_IDS', questionIds);
  },
  
  addSelectedQuestion({ commit }, questionId) {
    commit('ADD_SELECTED_QUESTION', questionId);
  },
  
  removeSelectedQuestion({ commit }, questionId) {
    commit('REMOVE_SELECTED_QUESTION', questionId);
  },
  
  toggleQuestionSelection({ state, commit }, questionId) {
    if (state.selectedQuestionIds.includes(questionId)) {
      commit('REMOVE_SELECTED_QUESTION', questionId);
    } else {
      commit('ADD_SELECTED_QUESTION', questionId);
    }
  },
  
  setQuestionDesiredAnswers({ commit }, { questionId, desiredAnswers }) {
    commit('SET_QUESTION_DESIRED_ANSWERS', { questionId, desiredAnswers });
  },
  
  setQuestionJustification({ commit }, { questionId, justification }) {
    commit('SET_QUESTION_JUSTIFICATION', { questionId, justification });
  },
  
  addQuestionAttachment({ commit }, { questionId, attachment }) {
    // Validate File object
    if (!attachment.file || !(attachment.file instanceof File)) {
      throw new Error('Ungültige Datei');
    }
    
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10 MB in bytes
    if (attachment.file.size > maxSize) {
      throw new Error('Datei ist zu groß. Maximum: 10 MB');
    }
    
    // Validate file type
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.mp4', '.doc', '.docx', '.xlsx', '.xls'];
    const fileName = attachment.file.name.toLowerCase();
    const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isAllowed) {
      throw new Error('Dateityp nicht unterstützt. Erlaubt: PDF, JPG, PNG, MP4, DOC, XLSX');
    }
    
    commit('ADD_QUESTION_ATTACHMENT', { questionId, attachment });
  },
  
  updateQuestionAttachment({ commit }, { questionId, attachmentIndex, attachment }) {
    commit('UPDATE_QUESTION_ATTACHMENT', { questionId, attachmentIndex, attachment });
  },
  
  removeQuestionAttachment({ commit }, { questionId, attachmentIndex }) {
    commit('REMOVE_QUESTION_ATTACHMENT', { questionId, attachmentIndex });
  },
  
  resetWizard({ commit }) {
    commit('RESET_WIZARD');
  },

  setConfirmation({ commit }, { key, value }) {
    commit('SET_CONFIRMATION', { key, value });
  },

  setSubmitting({ commit }, value) {
    commit('SET_IS_SUBMITTING', value);
  },

  setSubmissionError({ commit }, error) {
    commit('SET_SUBMISSION_ERROR', error);
  },

  setHasUploadFailures({ commit }, value) {
    commit('SET_HAS_UPLOAD_FAILURES', value);
  },

  setCreatedSurveyRevisionId({ commit }, id) {
    commit('SET_CREATED_SURVEY_REVISION_ID', id);
  },

  setCreatedEntryMappings({ commit }, mappings) {
    commit('SET_CREATED_ENTRY_MAPPINGS', mappings);
  },

  setQuestionAttachmentMandatory({ commit }, { questionId, isAttachmentMandatory }) {
    commit('SET_QUESTION_ATTACHMENT_MANDATORY', { questionId, isAttachmentMandatory });
  },

  setQuestionIsCommentPossible({ commit }, { questionId, isCommentPossible }) {
    commit('SET_QUESTION_IS_COMMENT_POSSIBLE', { questionId, isCommentPossible });
  },

  setQuestionHasChoices({ commit }, { questionId, hasChoices }) {
    commit('SET_QUESTION_HAS_CHOICES', { questionId, hasChoices });
  },

  setQuestionAiAnalysis({ commit }, { questionId, result }) {
    commit('SET_QUESTION_AI_ANALYSIS', { questionId, result });
  },

  setAttachmentUploadStatus({ commit }, payload) {
    commit('SET_ATTACHMENT_UPLOAD_STATUS', payload);
  },

  /**
   * Serializes the entire wizard state into a plain JSON-safe object.
   * File objects in attachments are converted to base64-encoded objects.
   */
  async serializeState({ state }) {
    const serialized = {
      version: DRAFT_SCHEMA_VERSION,
      currentStep: state.currentStep,
      inquiryType: state.inquiryType,
      selectedCheckId: state.selectedCheckId,
      surveyRevisionCreationLimitInDays: state.surveyRevisionCreationLimitInDays,
      selectedQuestionIds: [...state.selectedQuestionIds],
      wizardData: JSON.parse(JSON.stringify(state.wizardData)),
      questionComplaints: {},
      questionAttachmentMandatory: { ...state.questionAttachmentMandatory },
      questionIsCommentPossible: { ...state.questionIsCommentPossible },
      questionHasChoices: { ...state.questionHasChoices },
      questionAiAnalysis: JSON.parse(JSON.stringify(state.questionAiAnalysis)),
    };

    // Serialize each question's complaint data including attachments as base64
    for (const [questionId, complaint] of Object.entries(state.questionComplaints)) {
      const serializedAttachments = [];
      for (const attachment of (complaint.attachments || [])) {
        if (attachment.file instanceof File) {
          const base64Obj = await fileToBase64(attachment.file);
          serializedAttachments.push({
            ...attachment,
            file: null,
            serializedFile: base64Obj,
          });
        } else {
          serializedAttachments.push({ ...attachment });
        }
      }
      serialized.questionComplaints[questionId] = {
        desiredAnswers: [...(complaint.desiredAnswers || [])],
        justification: complaint.justification || '',
        attachments: serializedAttachments,
      };
    }

    return serialized;
  },

  /**
   * Saves the current wizard state as a draft on the server.
   * Requires wizardData from step 2 to contain ExternalClientId and ExternalClientLocationId.
   */
  async saveDraft({ dispatch, commit, state }) {
    commit('SET_IS_SAVING_DRAFT', true);
    commit('SET_DRAFT_SAVE_ERROR', null);

    try {
      const serializedState = await dispatch('serializeState');

      // Pass the internal SurveyInstanceId — the SP resolves all external IDs via REP joins
      const draft = {
        SurveyInstanceId: state.selectedCheckId,
        Content: JSON.stringify(serializedState),
      };

      const response = await ActionService.saveSurveyRevisionDraft(draft);
      commit('SET_IS_SAVING_DRAFT', false);
      return response;
    } catch (error) {
      commit('SET_DRAFT_SAVE_ERROR', error?.message || 'Fehler beim Speichern des Entwurfs');
      commit('SET_IS_SAVING_DRAFT', false);
      throw error;
    }
  },

  /**
   * Restores the wizard state from a draft's Content JSON string.
   * Reconstructs File objects from base64-encoded data.
   */
  restoreFromDraft({ commit }, draftContent) {
    const raw = typeof draftContent === 'string' ? JSON.parse(draftContent) : draftContent;
    const data = migrateDraft(raw);

    commit('RESET_WIZARD');
    commit('SET_INQUIRY_TYPE', data.inquiryType);
    commit('SET_SELECTED_CHECK', data.selectedCheckId);
    commit('SET_SURVEY_REVISION_CREATION_LIMIT_IN_DAYS', data.surveyRevisionCreationLimitInDays);
    commit('SET_SELECTED_QUESTION_IDS', data.selectedQuestionIds || []);

    // Restore wizardData for each step
    if (data.wizardData) {
      for (const [step, stepData] of Object.entries(data.wizardData)) {
        commit('SET_WIZARD_DATA', { step, data: stepData });
      }
    }

    // Restore question attachment mandatory flags
    if (data.questionAttachmentMandatory) {
      for (const [questionId, isAttachmentMandatory] of Object.entries(data.questionAttachmentMandatory)) {
        commit('SET_QUESTION_ATTACHMENT_MANDATORY', { questionId, isAttachmentMandatory });
      }
    }

    // Restore question comment possible flags
    if (data.questionIsCommentPossible) {
      for (const [questionId, isCommentPossible] of Object.entries(data.questionIsCommentPossible)) {
        commit('SET_QUESTION_IS_COMMENT_POSSIBLE', { questionId, isCommentPossible });
      }
    }

    // Restore question has-choices flags
    if (data.questionHasChoices) {
      for (const [questionId, hasChoices] of Object.entries(data.questionHasChoices)) {
        commit('SET_QUESTION_HAS_CHOICES', { questionId, hasChoices });
      }
    }

    // Restore AI analysis results
    if (data.questionAiAnalysis) {
      for (const [questionId, result] of Object.entries(data.questionAiAnalysis)) {
        commit('SET_QUESTION_AI_ANALYSIS', { questionId, result });
      }
    }

    // Restore question complaints with reconstructed File objects
    if (data.questionComplaints) {
      for (const [questionId, complaint] of Object.entries(data.questionComplaints)) {
        commit('SET_QUESTION_DESIRED_ANSWERS', { questionId, desiredAnswers: complaint.desiredAnswers || [] });
        commit('SET_QUESTION_JUSTIFICATION', { questionId, justification: complaint.justification || '' });

        for (const attachment of (complaint.attachments || [])) {
          let restoredAttachment = { ...attachment };
          if (attachment.serializedFile) {
            restoredAttachment.file = base64ToFile(attachment.serializedFile);
            delete restoredAttachment.serializedFile;
          }
          commit('ADD_QUESTION_ATTACHMENT', { questionId, attachment: restoredAttachment });
        }
      }
    }

    // Navigate to the step the user was on (minimum step 4, since drafts start from step 4)
    const targetStep = Math.max(data.currentStep || 4, 4);
    commit('SET_STEP', targetStep);
  },
};

const getters = {
  currentStep: (state) => state.currentStep,
  inquiryType: (state) => state.inquiryType,
  selectedCheckId: (state) => state.selectedCheckId,
  surveyRevisionCreationLimitInDays: (state) => state.surveyRevisionCreationLimitInDays,
  selectedQuestionIds: (state) => state.selectedQuestionIds,
  selectedQuestionCount: (state) => state.selectedQuestionIds.length,
  wizardData: (state) => state.wizardData,
  
  currentStepData: (state) => (step) => {
    return state.wizardData[step] || {};
  },
  
  canGoBack: (state) => {
    return state.currentStep > 2;
  },

  canCancel: (state) => {
    return state.currentStep <= 2;
  },
  
  canGoNext: (state, getters) => {
    // Basic validation - can be enhanced later
    if (state.currentStep === 1) {
      return !!state.selectedCheckId;
    }
    if (state.currentStep === 3) {
      // Step 3: At least 1 question must be selected
      return state.selectedQuestionIds.length > 0;
    }
    if (state.currentStep === 4) {
      // Step 4: All questions must be complete
      return getters.allQuestionsComplete;
    }
    return state.currentStep < 5;
  },
  
  getQuestionHasChoices: (state) => (questionId) => {
    return state.questionHasChoices[questionId] ?? false;
  },

  getQuestionAiAnalysis: (state) => (questionId) => {
    return state.questionAiAnalysis[questionId] || null;
  },

  getQuestionComplaint: (state) => (questionId) => {
    return state.questionComplaints[questionId] || {
      desiredAnswers: [],
      justification: '',
      attachments: [],
    };
  },
  
  /**
   * Returns true when the desired-answers field is valid for the given question.
   * Answer selection is only required for questions with more than one answer option.
   */
  isQuestionAnswersValid: (state, getters) => (questionId) => {
    const hasChoices = state.questionHasChoices[questionId] ?? false;
    if (!hasChoices) return true; // no answer options to select
    const complaint = getters.getQuestionComplaint(questionId);
    return !!(complaint.desiredAnswers && complaint.desiredAnswers.length > 0);
  },

  /**
   * Returns true when the justification meets the 50-character minimum.
   */
  isQuestionJustificationValid: (_state, getters) => (questionId) => {
    const complaint = getters.getQuestionComplaint(questionId);
    return !!(complaint.justification && complaint.justification.trim().length >= 50);
  },

  isQuestionComplete: (state, getters) => (questionId) => {
    if (!getters.isQuestionAnswersValid(questionId)) return false;
    if (!getters.isQuestionJustificationValid(questionId)) return false;

    // Check if at least one attachment exists when proof is required
    const isAttachmentMandatory = state.questionAttachmentMandatory[questionId] ?? false;
    if (isAttachmentMandatory) {
      const complaint = getters.getQuestionComplaint(questionId);
      if (!complaint.attachments || complaint.attachments.length === 0) return false;
    }

    return true;
  },
  
  allQuestionsComplete: (state, getters) => {
    return state.selectedQuestionIds.every(questionId => getters.isQuestionComplete(questionId));
  },

  // Step 5 getters
  confirmations: (state) => state.confirmations,

  allConfirmationsChecked: (state) => {
    return state.confirmations.correctness && state.confirmations.privacy && state.confirmations.noChanges;
  },

  isSubmitting: (state) => state.isSubmitting,

  submissionError: (state) => state.submissionError,

  hasUploadFailures: (state) => state.hasUploadFailures,

  createdSurveyRevisionId: (state) => state.createdSurveyRevisionId,

  createdEntryMappings: (state) => state.createdEntryMappings,

  totalAttachmentsCount: (state) => {
    let count = 0;
    Object.values(state.questionComplaints).forEach(complaint => {
      if (complaint.attachments) {
        count += complaint.attachments.length;
      }
    });
    return count;
  },

  failedAttachmentsCount: (state) => {
    let count = 0;
    Object.values(state.questionComplaints).forEach(complaint => {
      if (complaint.attachments) {
        complaint.attachments.forEach(attachment => {
          if (attachment.uploadStatus === 'failed') count++;
        });
      }
    });
    return count;
  },

  canSubmit: (state, getters) => {
    return getters.allConfirmationsChecked && getters.allQuestionsComplete && !state.isSubmitting;
  },

  cancelLabel: (state) => {
    if (state.currentStep === 1) {
      return 'Zurück zur Auswahl';
    }
    return null;
  },
  
  isLastStep: (state) => {
    return state.currentStep === 5;
  },

  isSavingDraft: (state) => state.isSavingDraft,

  draftSaveError: (state) => state.draftSaveError,

  showSaveDraft: (state) => {
    return state.currentStep >= 4 && state.createdSurveyRevisionId === null;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
